import { css, keyframes } from '@emotion/react';
import type { CSSInterpolation } from '@emotion/serialize';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { forwardRef, useState } from 'react';

import { checkMarkUrl } from '@adflow/frontend/ui-assets';
import { su, theme } from '@adflow/frontend/ui-theme';
import { useOnChange } from '@adflow/frontend/util-common';

const bounce = keyframes({
	'0%, 100%': {
		transform: 'scale(1, 1)',
	},
	'50%': {
		transform: 'scale(1.2, 1.2)',
	},
});

const styles = {
	container: css({
		position: 'relative',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'space-around',
		width: '1.25rem',
		height: '1.25rem',
	}),
	input: css({
		position: 'absolute',
		top: '0px',
		left: '0px',
		width: '100%',
		height: '100%',
		opacity: '0',
		cursor: 'pointer',
		'&:disabled': css({ cursor: 'not-allowed' }),
		'&[readonly]': css({ cursor: 'default' }),
	}),
	visibleIndicator: css(su.roundedMd, {
		borderWidth: '2px',
		borderStyle: 'solid',

		width: '1.25rem',
		height: '1.25rem',

		pointerEvents: 'none',

		backgroundColor: theme.colors.white,
		borderColor: theme.colors.palettes.primary.main,

		'input:checked + &': css({
			// Don't disable the border so that any `visibleIndicatorChildren`
			// elements do not change position between checked and unchecked.
			backgroundColor: theme.colors.palettes.primary.main,
			backgroundImage: `url("${checkMarkUrl}")`,
		}),

		'&[data-doing-animation="true"]': {
			animation: `${bounce} 0.2s both`,
		},

		'input:checked:disabled + &, input:checked[readonly] + &, input:checked[data-disabled="true"] + &':
			{
				backgroundColor: theme.colors.text.hint,
			},

		'input[readonly] + &': {
			borderColor: theme.colors.text.hint,
		},

		'input:disabled + &, input[data-disabled="true"] + &': {
			backgroundColor: theme.colors.input.disabledBackground,
			borderColor: theme.colors.text.hint,
		},
	}),
};

export interface CheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
	containerStyle?: CSSInterpolation;
	visibleIndicatorStyle?: CSSInterpolation;
	/**
	 * The width and hight of the input can be made larger so that it's
	 * easier to click without changing the visual size of the checkbox.
	 */
	className?: string;

	/** Useful for rendering icons or badges relative to the visual indicator. */
	visibleIndicatorChildren?: ReactNode;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
	function CheckboxRender(
		{
			className,
			containerStyle,
			visibleIndicatorStyle,
			visibleIndicatorChildren,
			...props
		},
		ref
	) {
		const [doingAnimation, setDoingAnimation] = useState(false);

		useOnChange(!!props.checked, () => {
			setDoingAnimation(true);
		});

		return (
			<div css={[styles.container, containerStyle]} className={className}>
				<input css={styles.input} type="checkbox" ref={ref} {...props} />
				<div
					css={[styles.visibleIndicator, visibleIndicatorStyle]}
					data-doing-animation={doingAnimation}
					onAnimationEnd={() => setDoingAnimation(false)}
				>
					{visibleIndicatorChildren}
				</div>
			</div>
		);
	}
);
